import React from 'react';
import { Move, Shield, Zap, Database, Globe, CheckCircle2 } from 'lucide-react';
import './WebsiteDevelopment.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

export default function WebsiteTransfer() {
  const features = [
    {
      icon: <Database size={24} />,
      title: 'Zero Data Loss',
      text: 'We ensure all your files, databases, and configuration settings are migrated safely without losing a single byte.'
    },
    {
      icon: <Zap size={24} />,
      title: 'Minimal Downtime',
      text: 'Our migration process is optimized to keep your website live and accessible throughout the entire transfer.'
    },
    {
      icon: <Shield size={24} />,
      title: 'Secure Transfer',
      text: 'Using encrypted protocols and secure environments, we protect your sensitive data during the migration process.'
    }
  ];

  const faqItems = [
    {
      question: 'Will my website be down during the migration?',
      answer: 'We aim for zero to minimal downtime. By setting up the new environment before switching DNS, your site remains accessible during the process.'
    },
    {
      question: 'How long does a typical migration take?',
      answer: 'Most migrations are completed within 24-48 hours, depending on the size of the website and the complexity of the database.'
    }
  ];

  return (
    <article className="website-page">
      <Hero 
        eyebrow="Website Transfer & Migration"
        title={<>Seamless <span className="hero-accent">Migration</span> with Zero Stress.</>}
        description="Moving your website shouldn't be a headache. Whether you're switching hosts or upgrading platforms, we handle the technical lifting for you."
        primaryAction={{ label: "Start Migration", link: "#features" }}
        stats={[
          { label: "Success Rate", value: "100%" },
          { label: "Downtime", value: "Near Zero" }
        ]}
      />

      <section className="section" id="features">
        <div className="section-heading">
          <span className="eyebrow">Expert Migration</span>
          <h2>Why trust us with your move?</h2>
          <p>We provide comprehensive migration services that ensure your digital assets are moved efficiently and securely.</p>
        </div>
        <div className="features-grid">
          {features.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="info-row">
          <div className="info-copy">
            <h3>Complete Data Integrity</h3>
            <p>Our experts meticulously handle every aspect of the transfer, from SSL certificates and email accounts to complex databases.</p>
            <ul className="check-list">
              <li><CheckCircle2 size={18} /> Full database migration</li>
              <li><CheckCircle2 size={18} /> SSL configuration</li>
              <li><CheckCircle2 size={18} /> Post-migration testing</li>
            </ul>
          </div>
          <div className="info-media">
            <img src="/images/website-transfer/migration.jpg" alt="Server Migration" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-heading">
          <span className="eyebrow">Deep Dive</span>
          <h2>Migration Questions.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection title="Ready to move to a better platform?" />
      <Footer />
    </article>
  );
}